import { SparklesIcon, CheckCircleIcon, ExclamationTriangleIcon, ArrowPathIcon } from '@heroicons/react/24/outline';
import { IUserModel, ImageGenerationStatus } from '@packages/global';
import { useTranslation } from 'react-i18next';
import { CompactModelSelector } from '../common/form';

interface TaskLike {
  status: ImageGenerationStatus;
  resultImageUrl?: string;
  resultMessage?: string;
}

interface AssetGeneratePreviewProps {
  models: IUserModel[];
  selectedModelId: string;
  modelsLoading: boolean;
  isGenerating: boolean;
  error: string | null;
  showPreview: boolean;
  existingImageUrl?: string;
  currentTask: TaskLike | null;
  generationHint: string;
  onModelChange: (id: string) => void;
}

export function AssetGeneratePreview({
  models, selectedModelId, modelsLoading,
  isGenerating, error, showPreview, existingImageUrl,
  currentTask, generationHint,
  onModelChange,
}: AssetGeneratePreviewProps) {
  const { t } = useTranslation(['projectSettings', 'common']);
  const renderPreview = () => {
    if (isGenerating) {
      return (
        <div className="flex-1 flex flex-col items-center justify-center bg-bg-form rounded-lg border border-border-subtle min-h-[280px]">
          <div className="w-14 h-14 border-4 border-primary/30 border-t-primary rounded-full animate-spin mb-3" />
          <div className="text-gray-50 font-medium text-sm mb-1">{t('asset.generatePreview.generating')}</div>
          <div className="text-xs text-gray-400">{generationHint}</div>
        </div>
      );
    }

    if (error && showPreview) {
      return (
        <div className="flex-1 flex flex-col items-center justify-center bg-red-500/5 rounded-lg border border-red-500/20 min-h-[280px] p-4">
          <ExclamationTriangleIcon className="h-10 w-10 text-red-500 mb-3" />
          <div className="text-red-500 font-medium text-sm mb-1">{t('asset.generatePreview.failed')}</div>
          <div className="text-xs text-red-400 text-center">{error}</div>
        </div>
      );
    }

    if (currentTask?.resultImageUrl) {
      return (
        <div className="flex-1 flex flex-col min-h-[280px]">
          <div className="flex items-center gap-1.5 mb-2">
            <CheckCircleIcon className="h-4 w-4 text-green-500" />
            <span className="text-xs text-green-500 font-medium">{t('asset.generatePreview.completed')}</span>
          </div>
          <div className="relative rounded-lg overflow-hidden border border-border-subtle flex-1">
            <img src={currentTask.resultImageUrl} alt={t('asset.generatePreview.generatedAlt')} className="w-full h-full object-contain" />
          </div>
        </div>
      );
    }

    return (
      <div className="flex-1 flex flex-col items-center justify-center bg-bg-form rounded-lg border border-border-subtle min-h-[280px]">
        {existingImageUrl ? (
          <img src={existingImageUrl} alt={t('asset.generatePreview.existingAlt')} className="w-full h-full object-contain rounded-lg" />
        ) : (
          <>
            <SparklesIcon className="h-10 w-10 text-gray-600 mb-2" />
            <div className="text-xs text-gray-500">{t('asset.generatePreview.placeholder')}</div>
          </>
        )}
      </div>
    );
  };

  return (
    <div className="flex-1 flex flex-col gap-3">
      <div className="flex-shrink-0">
        {modelsLoading ? (
          <div className="h-[42px] bg-bg-form rounded-lg animate-pulse" />
        ) : (
          <CompactModelSelector label={t('asset.generatePreview.selectModel')} models={models} value={selectedModelId} onChange={onModelChange} />
        )}
      </div>
      {renderPreview()}
    </div>
  );
}
